/**
 * This is the ProductActions component. It will be used to display purchase eligibility actions and offers to the user.
 *
 * @module views/components/ProductActions
 * @memberOf -Common
 */

import './ProductActions.scss';

import React, { useEffect, useRef } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import AsyncComponent from '@ulta/core/components/AsyncComponent/AsyncComponent';
import Button from '@ulta/core/components/Button/Button';
import InlineMessage from '@ulta/core/components/InlineMessage/InlineMessage';
import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import { useIntersectionProcessor } from '@ulta/core/hooks/useIntersectionProcessor/useIntersectionProcessor';
import {
  RESOURCE_CATEGORY,
  useResourceLoaderContext
} from '@ulta/core/providers/ResourceLoaderProvider/ResourceLoaderProvider';
import { hasItems } from '@ulta/core/utils/array/array';
import { isServer } from '@ulta/core/utils/device_detection/device_detection';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import NonCachedPageQuery from '@ulta/core/utils/graphql/queries/cms/noncachedcms';

import AddToBagFavorite from '@ulta/components/AddToBagFavorite/AddToBagFavorite';
import OutofStockFavorite from '@ulta/components/OutOfStockFavorite/OutOfStockFavorite';
import ProductComingSoon from '@ulta/components/ProductComingSoon/ProductComingSoon';
import ProductFulfillmentOptions from '@ulta/components/ProductFulfillmentOptions/ProductFulfillmentOptions';
import ProductInStore from '@ulta/components/ProductInStore/ProductInStore';
import ProductPreSale from '@ulta/components/ProductPresale/ProductPresale';
import RoundedContainer from '@ulta/components/RoundedContainer/RoundedContainer';

import * as utils from './ProductActions';

/**
 * Represents a ProductActions component
 *
 * @method
 * @param {ProductActionsProps} props - React properties passed from composition
 * @returns ProductActions
 */
const ProductActions = ( props ) => {
  const { queueResource, resourceLoaded } = useResourceLoaderContext();

  const {
    addToBagAction,
    bookmarkAction,
    bookmarked,
    comingSoon,
    default: defaultStateData,
    id,
    includeDivider,
    inStore,
    outOfStock,
    perk,
    productId,
    removeBookmarkAction,
    skuId,
    state,
    storeAvailabilityAction,
    favoriteId,
    commerceId,
    invokeMutation,
    quantity,
    messages,
    componentKey,
    fulfillmentOptions
  } = props;
  if( isServer() ){
    queueResource( { category: RESOURCE_CATEGORY.PriorityGraphQL } );
  }

  useEffect( () => {
    resourceLoaded( { category: RESOURCE_CATEGORY.PriorityGraphQL } );
  }, [] );

  utils.useUpdateProductAction( props );

  const components = utils.getRenderState( {
    state,
    defaultStateData,
    outOfStock,
    fulfillmentOptions,
    storeAvailabilityAction,
    inStore
  } );

  const configVariables = {
    moduleParams: {
      productId,
      skuId
    }
  };
  const componentRef = useRef();
  useIntersectionProcessor( { ...props, componentRef } );

  return (
    <div
      className={ classNames( 'ProductActions', {
        'ProductActions--bottom-spacer': includeDivider
      } ) }
      id={ id }
      ref={ componentRef }
    >
      { components.ProductFulfillmentOptions && (
        <div className='ProductActions--bottom-spacer'>
          <RoundedContainer layout='horizontal'>
            <ProductFulfillmentOptions
              layout={ 'vertical' }
              items={ fulfillmentOptions?.items }
              config={ configVariables }
            />
          </RoundedContainer>
        </div>
      ) }
      { components.ProductComingSoon && (
        <ProductComingSoon
          { ...spreadProps( { ...comingSoon } ) }
          bookmarked={ bookmarked }
          bookmarkAction={ bookmarkAction }
          removeBookmarkAction={ removeBookmarkAction }
          { ...spreadProps( { ...props } ) }
        />
      ) }

      { components.ProductPreSale && (
        <ProductPreSale
          { ...spreadProps( { ...perk } ) }
          bookmarked={ bookmarked }
          bookmarkAction={ bookmarkAction }
          removeBookmarkAction={ removeBookmarkAction }
          { ...spreadProps( { ...props } ) }
        />
      ) }

      { components.NotifyMe && (
        <AsyncComponent
          moduleName='NotifyMe'
          { ...spreadProps( { ...outOfStock } ) }
          bookmarked={ bookmarked }
          bookmarkAction={ bookmarkAction }
          commerceId={ commerceId }
          id={ id }
          invokeMutation={ invokeMutation }
          productId={ productId }
          removeBookmarkAction={ removeBookmarkAction }
          skuId={ skuId }
          quantity={ quantity }
          state={ state }
          favoriteId={ favoriteId }
          comingSoon={ comingSoon }
        />
      ) }

      { components.OutofStockFavorite && (
        <OutofStockFavorite
          { ...spreadProps( { ...outOfStock } ) }
          addToBagAction={ addToBagAction }
          bookmarked={ bookmarked }
          bookmarkAction={ bookmarkAction }
          removeBookmarkAction={ removeBookmarkAction }
          comingSoon={ comingSoon }
          { ...spreadProps( { ...props } ) }
        />
      ) }

      { components.InStore && (
        <ProductInStore
          storeAvailabilityAction={ storeAvailabilityAction }
          config={ configVariables }
          inStore={ inStore }
          { ...spreadProps( { ...props } ) }
        />
      ) }

      { components.AddToBagFavorite && <AddToBagFavorite { ...spreadProps( { ...props } ) } /> }

      { messages?.length > 0 &&
        messages.map( ( messageItem, index ) => (
          <div key={ `${index}-${componentKey}` }
            className='ProductActions--info-message'
          >
            <InlineMessage
              message={ messageItem.message }
              messageType={ messageItem.icon }
              { ...( messageItem.icon === 'error' && { role:'alert' } ) }
            ></InlineMessage>
          </div>
        ) ) }

      { components.CheckStoreAvailability && (
        <CheckStoreAvailability
          storeAvailabilityAction={ storeAvailabilityAction }
          config={ configVariables }
        />
      ) }

      { components.ViewItemsInOffer && (
        <div
          className={ classNames( 'viewItemsinOffer', {
            'viewItemsinOffer--sellable': defaultStateData?.sellable
          } ) }
        >
          <ViewItemsInOffer
            viewItemsInOfferAction={ defaultStateData?.viewItemsInOfferAction }
          />
        </div>
      ) }
    </div>
  );
};

/**
 * Handles requests
 *
 * @param {ProductActionsProps} props props
 */
export const useUpdateProductAction = ( props ) => {
  useEffect( () => {
    if( isServer() ){
      return;
    }

    const { id, invokeAction } = props;
    if( !id || !invokeAction ){
      return;
    }

    /**
     * Refresh Product Page
     */
    const refreshProductPage = () => {
      const action = {
        graphql: NonCachedPageQuery,
        // TODO customheaders will need to come from dxl definition in future
        customHeaders: [
          { key: 'X-ULTA-GRAPH-TYPE', value: 'query' },
          { key: 'X-ULTA-GRAPH-MODULE-NAME', value: 'ProductActions' },
          { key: 'X-ULTA-GRAPH-SUB-TYPE', value: 'noncachePage' }
        ],
        params: ['gti', 'loginStatus'],
        variables: {
          contentId: id,
          moduleParams: {
            ...props
          }
        }
      };
      invokeAction( action );
    };

    document.addEventListener( 'removeItemFromMiniCart', refreshProductPage );
    return () => {
      document.removeEventListener(
        'removeItemFromMiniCart',
        refreshProductPage
      );
    };
  }, [] );
};

/**
 * @const {object} RESPONSE_STATE - Map of components to render based on ProductActions state
 */
export const RESPONSE_STATE = {
  ComingSoon: 'coming_soon',
  Perk: 'perk',
  OutOfStock: 'out_of_stock',
  InStore: 'in_store',
  Default: 'default'
};

/**
 * Determines which components should render
 * @param {object} data args
 * @param {string} data.state render switch
 * @param {object} data.defaultStateData status of the 'default' (sellable) state
 * @param {object} data.outOfStock status of the 'outOfStock' state
 * @returns {object} object of components to render
 */
export const getRenderState = ( data ) => {
  const {
    state = RESPONSE_STATE.Default,
    defaultStateData = {},
    outOfStock = {},
    fulfillmentOptions = {},
    storeAvailabilityAction,
    inStore
  } = data || {};

  const components = {
    ProductFulfillmentOptions: false,
    ProductComingSoon: false,
    ProductPreSale: false,
    NotifyMe: false,
    OutofStockFavorite: false,
    InStore: false,
    AddToBagFavorite: false,
    CheckStoreAvailability: false,
    ViewItemsInOffer: false
  };

  const isSellable = !!defaultStateData?.sellable;
  const hasOfferLabel = !!defaultStateData?.viewItemsInOfferAction?.label;
  const outOfStockNotify = !!outOfStock?.notify;
  const hasProductFulfillmentOptions = hasItems( fulfillmentOptions?.items );

  switch ( state ){
    case RESPONSE_STATE.ComingSoon:
      return { ...components, ProductComingSoon: true };

    case RESPONSE_STATE.Perk:
      return { ...components, ProductPreSale: true };

    case RESPONSE_STATE.OutOfStock:
      return {
        ...components,
        NotifyMe: outOfStockNotify,
        OutofStockFavorite: !outOfStockNotify,
        ProductFulfillmentOptions: hasProductFulfillmentOptions
      };

    case RESPONSE_STATE.InStore:
      return {
        ...components,
        InStore: !!inStore,
        ProductFulfillmentOptions: hasProductFulfillmentOptions
      };

    default:
      return {
        ...components,
        ProductFulfillmentOptions: hasProductFulfillmentOptions,
        AddToBagFavorite: isSellable,
        ViewItemsInOffer: hasOfferLabel,
        CheckStoreAvailability: !!storeAvailabilityAction
      };
  }
};

/**
 * Function to return check in-store availability button.
 *
 * @method CheckStoreAvailability
 * @param {object} action - This includes action and label for link button.
 * @returns Button
 **/
export const CheckStoreAvailability = ( props ) => {
  const buttonRef = useRef();

  if( !props?.storeAvailabilityAction ){
    return null;
  }

  const action = {
    ...props.storeAvailabilityAction,
    variables: props.config
  };

  return (
    <Button
      likeLink
      label={ props?.storeAvailabilityAction?.label }
      className='inStoreAvailability'
      action={ action }
      ref={ buttonRef }
      crossButtonVisibility={ false }
    />
  );
};

/**
 * Function to return view items offer button.
 *
 * @method ViewItemsInOffer
 * @param {object} action - This includes action and label for link button.
 * @returns Button
 **/
export const ViewItemsInOffer = ( props ) => {
  if( !props?.viewItemsInOfferAction ){
    return null;
  }

  return (
    <Link_Huge likeButtonOutline
      action={ props.viewItemsInOfferAction }
    >
      { props?.viewItemsInOfferAction?.label }
    </Link_Huge>
  );
};

/*
 /**
  * Property type definitions
  * @typedef ProductActionsProps
  * @type {object}
  *
  * @property {function} invokeAction - invokeAction, calls DXL
  * @property {string} state - Sets the key for product action to be displayed
  * @property {object} bookmarkAction - Label to be displayed
  * @property {object} removeBookmarkAction - Label to remove from bag
  * @property {boolean} bookmarked - True/False value for the bookmarked
  * @property {object} storeAvailabilityAction - Sets the label for storeAvailabilityAction
  * @property {object} addToBagAction - Sets label and graphql
  * @property {object} removeFromBagAction - Sets label and graphql
  * @property {string} successLabel - Sets success Label
  * @property {string} inBagLabel - Sets in bag Label
  * @property {number} quantity - Sets quantity
  * @property {number} addedQuantity - Sets added quantity
  * @property {string} email - Sets email label
  * @property {string} commerceId - Sets commerceid label
  * @property {string} skuId - Sets skuId value
  * @property {string} productId - Sets productId value
  * @property {object} default - Sets default object value
  * @property {boolean} sellable - Sets sellable to true/false
  * @property {boolean} disabled - Sets disabled to true/false
  * @property {number} maxQuantity - Sets maxQuantity
  * @property {string} errorLabel - Sets error Label
  * @property {object} viewItemsInOfferAction - Sets action object for items in offer
  * @property {string} selectQuantityLabel - Sets select Quantity label
  * @property {object} inStore - Sets object value for instore products
  * @property {string} inStoreOnlyLabel - Sets label for instore only
  * @property {object} comingSoon - Sets object for coming soon products
  * @property {string} comingSoonLabel - Sets label for coming soon
  * @property {string} comingSoonMessage - Sets message for coming soon message
  * @property {object} outOfStock - Sets object for out of stock products
  * @property {boolean} notify - Sets true/false for notify
  * @property {string} notifyMeMessage - Sets label for notify me message
  * @property {string} enterEmailLabel - Sets label for  email
  * @property {string} emailErrorLabel - Sets email error label
  * @property {object} notifyMeAction - Sets notify me action
  * @property {boolean} notifyMeDisabled - Sets true/false for notify me
  * @property {string} outOfStockLabel - Sets out of stock label
  * @property {object} perk - Sets action object for perk products
  * @property {string} perkLabel - Sets the label for the product presale
  * @property {string} perkMessage - Sets the perkMessage property
  * @property {object} perkAction - Sets the action label and url for product presale
  * @property {string} searchTitleLabel - Sets the search title label
  * @property {object} image - Sets the image
  * @property {string} brandName - Sets the brand name
  * @property {string} productName - Sets the product name
  * @property {string} variantTypeLabel - Sets the variant type label
  * @property {string} variantLabel - Sets the variant label
  * @property {string} searchPlaceholderLabel - Sets the search placeholder label
  * @property {string} clearSearchAccessibilityLabel - Sets the clear search acessibility label
  * @property {string} searchAccessibilityLabel - Sets the search accessibility label
  * @property {string} currentLocationAccessibilityLabel - Sets the current location accessibility label
  * @property {object} searchAction - Sets the search action
  * @property {string} helperLabel - Sets the helper label
  * @property {string} badLocationLabel - Sets the bad location label
  * @property {string} countLabel - Sets the count label
  * @property {object} stores - Sets the stores
  * @property {object} closeAccessibilityAction - Sets the close accessibility action
  * @property {string} cancelLabel - Sets cancel label
  * @property {string} id - Sets the id string
  * @property {string} favoriteId - Sets the bookmark id
  */
export const propTypes = {
  invokeAction: PropTypes.func,
  state: PropTypes.string,
  bookmarkAction: PropTypes.shape( {
    label: PropTypes.string,
    graphql: PropTypes.string
  } ),
  removeBookmarkAction: PropTypes.shape( {
    label: PropTypes.string,
    url: PropTypes.string
  } ),
  bookmarked: PropTypes.bool,
  storeAvailabilityAction: PropTypes.shape( {
    label: PropTypes.string
  } ),
  fulfillmentOptions: PropTypes.shape( {
    items: PropTypes.array
  } ),
  addToBagAction: PropTypes.shape( {
    label: PropTypes.string,
    graphql: PropTypes.string
  } ),
  removeFromBagAction: PropTypes.shape( {
    label: PropTypes.string,
    graphql: PropTypes.string
  } ),
  successLabel: PropTypes.string,
  inBagLabel: PropTypes.string,
  quantity: PropTypes.number,
  addedQuantity: PropTypes.number,
  email: PropTypes.string,
  commerceId: PropTypes.string,
  skuId: PropTypes.string,
  productId: PropTypes.string,
  default: PropTypes.shape( {
    sellable: PropTypes.bool,
    disabled: PropTypes.bool,
    maxQuantity: PropTypes.number,
    errorLabel: PropTypes.string,
    viewItemsInOfferAction: PropTypes.shape( {
      label: PropTypes.string,
      graphql: PropTypes.string
    } ),
    cancelLabel: PropTypes.string,
    selectQuantityLabel: PropTypes.string
  } ),
  inStore: PropTypes.shape( {
    inStoreOnlyLabel: PropTypes.string
  } ),
  comingSoon: PropTypes.shape( {
    comingSoonLabel: PropTypes.string,
    comingSoonMessage: PropTypes.string
  } ),
  outOfstock: PropTypes.shape( {
    notify: PropTypes.bool,
    notifyMeMessage: PropTypes.string,
    enterEmailLabel: PropTypes.string,
    emailErrorLabel: PropTypes.string,
    notifyMeAction: PropTypes.shape( {
      label: PropTypes.string,
      graphql: PropTypes.string
    } ),
    notifyMeDisabled: PropTypes.bool,
    outOfStockLabel: PropTypes.string
  } ),
  perk: PropTypes.shape( {
    perkLabel: PropTypes.string,
    perkMessage: PropTypes.string,
    perkAction: PropTypes.shape( {
      label: PropTypes.string,
      url: PropTypes.string
    } )
  } ),
  favoriteId: PropTypes.string,
  searchTitleLabel: PropTypes.string,
  image: PropTypes.object,
  brandName: PropTypes.string,
  productName: PropTypes.string,
  variantTypeLabel: PropTypes.string,
  variantLabel: PropTypes.string,
  searchPlaceholderLabel: PropTypes.string,
  clearSearchAccessibilityLabel: PropTypes.string,
  searchAccessibilityLabel: PropTypes.string,
  currentLocationAccessibilityLabel: PropTypes.string,
  searchAction: PropTypes.object,
  helperLabel: PropTypes.string,
  badLocationLabel: PropTypes.string,
  countLabel: PropTypes.string,
  stores: PropTypes.array,
  closeAccessibilityAction: PropTypes.shape( {
    label: PropTypes.string,
    graphql: PropTypes.string
  } ),
  cancelLabel: PropTypes.string,
  id: PropTypes.string
};

ProductActions.propTypes = propTypes;

export default ProductActions;
